/**
 * AI Service for Layer Generation using Claude API
 * Handles API key management, layer generation requests, and response parsing
 */

const AI_SERVICE = {
    PROVIDER_STORAGE_KEY: 'ai_provider',
    MODEL_STORAGE_KEY: 'ai_model',
    // Use local proxy server to avoid CORS issues
    API_ENDPOINT: 'http://localhost:3001/api/ai/generate',
    PROXY_ENABLED: true,

    /**
     * Get the current AI provider
     * @returns {string} 'claude' or 'openai'
     */
    getProvider() {
        return localStorage.getItem(this.PROVIDER_STORAGE_KEY) || 'claude';
    },

    /**
     * Set the AI provider
     * @param {string} provider - 'claude' or 'openai'
     */
    setProvider(provider) {
        if (provider === 'claude' || provider === 'openai') {
            localStorage.setItem(this.PROVIDER_STORAGE_KEY, provider);
        }
    },

    /**
     * Get the stored model from localStorage
     * @returns {string|null} The model identifier or null if not set
     */
    getModel() {
        return localStorage.getItem(this.MODEL_STORAGE_KEY);
    },

    /**
     * Save the model to localStorage
     * @param {string} model - The Claude model identifier
     */
    setModel(model) {
        if (model && model.trim()) {
            localStorage.setItem(this.MODEL_STORAGE_KEY, model.trim());
        }
    },

    /**
     * Remove the model from localStorage
     */
    clearModel() {
        localStorage.removeItem(this.MODEL_STORAGE_KEY);
    },

    /**
     * Generate a layer using AI based on user prompt
     * API keys are managed server-side for security
     * @param {string} prompt - User's description of the layer to generate
     * @returns {Promise<Object>} Layer object with name, geojson, metadata, and fields
     */
    async generateLayer(prompt) {
        const provider = this.getProvider();
        const model = this.getModel();

        if (!model) {
            throw new Error('No AI model selected. Please select a model in settings.');
        }

        try {
            // Use proxy server (API keys are server-side)
            const response = await fetch(this.API_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    prompt: prompt,
                    model: model,
                    provider: provider
                    // No apiKey - server uses environment variables
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                console.error('API Error Details:', errorData);
                const errorMessage = errorData.error ||
                    errorData.details?.error?.message ||
                    `API request failed: ${response.status} ${response.statusText}`;
                throw new Error(errorMessage);
            }

            const data = await response.json();

            // Extract the text content from Claude's response
            const content = data.content[0].text;

            // Parse the JSON response
            let layerData;
            let cleanContent; // Declare outside try block for error logging
            try {
                // Remove markdown code blocks if present
                cleanContent = content.replace(/```json\n?|\n?```/g, '').trim();

                // Remove thousand separators from numbers (e.g., 6,680,000 -> 6680000)
                // Only remove commas that have digits on both sides (thousand separators)
                cleanContent = cleanContent.replace(/(\d),(\d)/g, '$1$2');

                console.log('Attempting to parse cleaned JSON (first 200 chars):', cleanContent.substring(0, 200));

                layerData = JSON.parse(cleanContent);
            } catch (parseError) {
                console.error('Parse error details:', parseError.message);

                // Extract position from error message
                const posMatch = parseError.message.match(/position (\d+)/);
                if (posMatch) {
                    const pos = parseInt(posMatch[1]);
                    const start = Math.max(0, pos - 50);
                    const end = Math.min(cleanContent.length, pos + 50);
                    console.error(`Problem area (positions ${start}-${end}):`, cleanContent.substring(start, end));
                    console.error(`Character at error position: "${cleanContent[pos]}" (code: ${cleanContent.charCodeAt(pos)})`);
                }

                console.error('Full cleaned content:', cleanContent);
                throw new Error(`AI generated invalid JSON: ${parseError.message}`);
            }

            // Validate the response structure
            this.validateLayerData(layerData);

            return layerData;

        } catch (error) {
            console.error('AI layer generation error:', error);
            throw error;
        }
    },

    /**
     * Validate the AI-generated layer data structure
     * @param {Object} layerData - The layer data to validate
     * @throws {Error} If validation fails
     */
    validateLayerData(layerData) {
        if (!layerData.layerName || typeof layerData.layerName !== 'string') {
            throw new Error('Invalid layer data: missing or invalid layerName');
        }

        if (!['Point', 'LineString', 'Polygon'].includes(layerData.geometryType)) {
            throw new Error('Invalid layer data: geometryType must be Point, LineString, or Polygon');
        }

        if (!layerData.geojson || layerData.geojson.type !== 'FeatureCollection') {
            throw new Error('Invalid layer data: geojson must be a FeatureCollection');
        }

        if (!Array.isArray(layerData.geojson.features) || layerData.geojson.features.length === 0) {
            throw new Error('Invalid layer data: geojson must contain at least one feature');
        }

        if (!Array.isArray(layerData.fields)) {
            throw new Error('Invalid layer data: fields must be an array');
        }

        // Validate each feature
        layerData.geojson.features.forEach((feature, index) => {
            if (feature.type !== 'Feature') {
                throw new Error(`Invalid feature at index ${index}: type must be "Feature"`);
            }
            if (!feature.geometry || !feature.geometry.type || !feature.geometry.coordinates) {
                throw new Error(`Invalid feature at index ${index}: missing or invalid geometry`);
            }
            if (feature.geometry.type !== layerData.geometryType) {
                throw new Error(`Invalid feature at index ${index}: geometry type mismatch`);
            }
        });
    },

    /**
     * Convert AI-generated layer data into the format expected by layerManager
     * @param {Object} aiLayerData - Layer data from AI
     * @returns {Object} Formatted layer data
     */
    formatLayerForManager(aiLayerData) {
        const { layerName, geometryType, fields, geojson } = aiLayerData;

        // Build field types and default values objects
        const fieldTypes = {};
        const defaultFields = {};

        fields.forEach(field => {
            fieldTypes[field.name] = field.type;
            if (field.defaultValue !== undefined && field.defaultValue !== null) {
                defaultFields[field.name] = field.defaultValue;
            }
        });

        // Calculate bounds from features
        const bounds = this.calculateBounds(geojson.features);

        // Add feature indices
        geojson.features.forEach((feature, index) => {
            feature.properties = feature.properties || {};
            feature.properties.__featureIndex = index;
        });

        return {
            name: layerName,
            geojson: geojson,
            featureCount: geojson.features.length,
            metadata: {
                geometryTypes: [geometryType],
                geometryType: geometryType,
                createdAt: new Date().toISOString(),
                source: 'ai-generated',
                defaultFields: defaultFields,
                fieldTypes: fieldTypes,
                bounds: bounds
            }
        };
    },

    /**
     * Calculate bounding box from features
     * @param {Array} features - GeoJSON features
     * @returns {Object} Bounds object with minLon, maxLon, minLat, maxLat
     */
    calculateBounds(features) {
        let minLon = Infinity, maxLon = -Infinity;
        let minLat = Infinity, maxLat = -Infinity;

        features.forEach(feature => {
            const coords = feature.geometry.coordinates;

            const processCoord = (coord) => {
                if (Array.isArray(coord[0])) {
                    coord.forEach(processCoord);
                } else {
                    const [lon, lat] = coord;
                    minLon = Math.min(minLon, lon);
                    maxLon = Math.max(maxLon, lon);
                    minLat = Math.min(minLat, lat);
                    maxLat = Math.max(maxLat, lat);
                }
            };

            processCoord(coords);
        });

        return { minLon, maxLon, minLat, maxLat };
    }
};

export default AI_SERVICE;
